import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import {
  abandonSession,
  ensureSessionFresh,
  supabase,
} from "@/lib/supabase";

export interface NhanVien {
  id: string;
  email: string;
  ho_ten: string;
  chuc_vu: string;
  thoi_gian_tao: string;
}

interface AuthContextValue {
  user: User | null;
  nhanVien: NhanVien | null;
  loading: boolean;
  /** Đang tải hồ sơ nhân viên (chưa biết chức vụ) */
  profileLoading: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Khoảng thời gian kiểm tra lại phiên đăng nhập (mili giây). */
const SESSION_CHECK_INTERVAL = 60000;

/** So sánh hồ sơ để không tạo object mới khi dữ liệu không đổi. */
function isSameProfile(a: NhanVien | null, b: NhanVien | null): boolean {
  if (!a || !b) return a === b;
  return (
    a.id === b.id &&
    a.email === b.email &&
    a.ho_ten === b.ho_ten &&
    a.chuc_vu === b.chuc_vu &&
    a.thoi_gian_tao === b.thoi_gian_tao
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [nhanVien, setNhanVien] = useState<NhanVien | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  // Hồ sơ đã tải xong cho tài khoản nào + đang tải cho tài khoản nào.
  // Nhờ vậy các sự kiện gia hạn token lặp lại KHÔNG kéo theo việc tải lại hồ sơ
  // và không làm cả trang bị "trắng" rồi dựng lại.
  const loadedForRef = useRef<string | null>(null);
  const inflightRef = useRef<string | null>(null);

  const fetchNhanVien = useCallback(async (userId: string) => {
    if (inflightRef.current === userId) return;
    inflightRef.current = userId;

    try {
      const { data, error } = await supabase
        .from("nhan_vien")
        .select("*")
        .eq("id", userId)
        .maybeSingle();

      if (error || !data) {
        return;
      }

      loadedForRef.current = userId;
      const next = data as NhanVien;
      setNhanVien((prev) => (isSameProfile(prev, next) ? prev : next));
    } catch {
      // Bỏ qua lỗi mạng, không để lỗi tràn ra ngoài
    } finally {
      if (inflightRef.current === userId) inflightRef.current = null;
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let intervalId: number | undefined;

    const resetToGuest = () => {
      if (!mounted) return;
      loadedForRef.current = null;
      setUser(null);
      setNhanVien(null);
      setProfileLoading(false);
      setLoading(false);
    };

    /** Kiểm tra phiên lúc khởi động: tự gia hạn hoặc dọn phiên hỏng. */
    const bootstrap = async () => {
      const result = await ensureSessionFresh();
      if (!mounted) return;

      if (result.status !== "ok") {
        resetToGuest();
        return;
      }

      setUser(result.session.user);
      setLoading(false);
      void fetchNhanVien(result.session.user.id);
    };

    /**
     * Giữ phiên luôn tươi: chỉ gia hạn token, không gọi lại hồ sơ nhân viên.
     * Nếu token đã bị server thu hồi -> đưa về trạng thái chưa đăng nhập.
     */
    const keepAlive = async () => {
      const result = await ensureSessionFresh();
      if (!mounted) return;
      if (result.status !== "ok") {
        resetToGuest();
      }
    };

    // Listener đăng ký trước khi kiểm tra phiên để không bỏ lỡ SIGNED_OUT
    const { data: listener } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (event === "SIGNED_OUT" || event === "USER_DELETED") {
          loadedForRef.current = null;
          setUser(null);
          setNhanVien(null);
          setProfileLoading(false);
          return;
        }

        setUser(nextSession?.user ?? null);

        const userId = nextSession?.user?.id;
        if (!userId) {
          loadedForRef.current = null;
          setNhanVien(null);
          setProfileLoading(false);
          return;
        }

        // Chỉ tải hồ sơ khi thật sự chưa có (lần đầu hoặc đổi tài khoản).
        // Các sự kiện gia hạn token (TOKEN_REFRESHED...) không được làm trắng trang.
        if (loadedForRef.current === userId) return;

        setProfileLoading(true);
        void fetchNhanVien(userId);
      }
    );

    void bootstrap();

    intervalId = window.setInterval(() => {
      void keepAlive();
    }, SESSION_CHECK_INTERVAL);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void keepAlive();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      mounted = false;
      if (intervalId) window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      listener.subscription.unsubscribe();
    };
  }, [fetchNhanVien]);

  const signIn = useCallback(async (email: string, password: string) => {
    // Xoá phiên cũ (nếu có) trước khi đăng nhập để không dính token hỏng
    await abandonSession();

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) {
      return { error: error.message };
    }
    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    await abandonSession();
    loadedForRef.current = null;
    setNhanVien(null);
    setUser(null);
  }, []);

  // Giữ nguyên object context khi dữ liệu không đổi: hạn chế mọi trang con
  // phải vẽ lại mỗi khi provider render.
  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      nhanVien,
      loading,
      profileLoading,
      signIn,
      signOut,
    }),
    [user, nhanVien, loading, profileLoading, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}